import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Box,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Person,
  DirectionsCar,
  Assignment,
  Description,
  ContactPhone,
  Save,
  MoreVert,
  Email,
  Phone,
  LocationOn,
  ArrowBack,
} from "@mui/icons-material";
import {
  Card,
  Grid,
  Typography,
  Divider,
  Button,
  SidebarWrapper,
  StyledListItemButton,
  Spacer,
  AboutIcon,
} from "./style";

const ExampleCRM = () => {
  const navigate = useNavigate();
  const [activeItem, setActiveItem] = useState("Cliente Info");

  const menuItems = [
    { text: "Cliente Info", icon: <Person /> },
    { text: "Veicoli", icon: <DirectionsCar /> },
    { text: "Preventivi", icon: <Assignment /> },
    { text: "Documenti", icon: <Description /> },
    { text: "Contatti", icon: <ContactPhone /> },
  ];

  const renderContent = () => {
    switch (activeItem) {
      case "Cliente Info":
        return <ClienteInfoView />;
      case "Veicoli":
        return <VeicoliView />;
      case "Preventivi":
        return <PreventiviView />;
      case "Contatti":
        return <ContattiView />;
      default:
        return (
          <Box py={10} textAlign="center">
            <Typography variant="h6" color="textSecondary">
              Sezione "{activeItem}" in fase di sviluppo
            </Typography>
          </Box>
        );
    }
  };

  return (
    <React.Fragment>
      <Helmet title="Cliente CRM" />

      {/* Header Area */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="flex-start"
        mb={4}
      >
        <Box display="flex" alignItems="center" gap={4}>
          <Tooltip title="Torna indietro">
            <IconButton onClick={() => navigate(-1)} sx={{ mb: 1 }}>
              <ArrowBack />
            </IconButton>
          </Tooltip>
          <Box>
            <Typography variant="h3" gutterBottom>
              Mario Rossi
            </Typography>
            <Box display="flex" gap={3}>
              <Typography
                variant="body2"
                color="textSecondary"
                display="flex"
                alignItems="center"
              >
                <AboutIcon>
                  <Person />
                </AboutIcon>{" "}
                RSSMRA80A01H501U
              </Typography>
              <Typography
                variant="body2"
                color="textSecondary"
                display="flex"
                alignItems="center"
              >
                <AboutIcon>
                  <LocationOn />
                </AboutIcon>{" "}
                Roma (RM)
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box display="flex" alignItems="center" gap={3}>
          <Box textAlign="right">
            <Typography variant="subtitle1" fontWeight="bold">
              Agente: Vincenzo Peluso
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Mobility Express S.r.l.
            </Typography>
          </Box>
          <Avatar
            sx={{
              width: 48,
              height: 48,
              bgcolor: "primary.main",
              fontSize: "1.2rem",
            }}
          >
            VP
          </Avatar>
        </Box>
      </Box>

      <Divider mb={6} />

      <Grid container spacing={6}>
        {/* Left Sidebar Menu */}
        <Grid item xs={12} md={3} lg={2.5}>
          <SidebarWrapper>
            <List disablePadding>
              {menuItems.map((item) => (
                <ListItem key={item.text} disablePadding>
                  <StyledListItemButton
                    active={activeItem === item.text ? 1 : 0}
                    onClick={() => setActiveItem(item.text)}
                  >
                    <ListItemIcon
                      sx={{
                        color:
                          activeItem === item.text ? "primary.main" : "inherit",
                        minWidth: 40,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      primaryTypographyProps={{
                        fontWeight:
                          activeItem === item.text ? "bold" : "normal",
                        color:
                          activeItem === item.text ? "primary.main" : "inherit",
                      }}
                    />
                  </StyledListItemButton>
                </ListItem>
              ))}
            </List>
          </SidebarWrapper>
        </Grid>

        {/* Main Content Area */}
        <Grid item xs={12} md={9} lg={9.5}>
          <Card>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={6}
              >
                <Typography variant="h5" fontWeight="bold">
                  {activeItem}
                </Typography>
                <Box display="flex" gap={2}>
                  <Button variant="contained" startIcon={<Save />}>
                    Salva Modifiche
                  </Button>
                </Box>
              </Box>

              <Box>{renderContent()}</Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </React.Fragment>
  );
};

// --- Sub-views components ---

const ClienteInfoView = () => (
  <Grid container spacing={4}>
    <Grid item xs={12}>
      <Typography variant="h6" color="primary" gutterBottom>
        Dati Anagrafici
      </Typography>
      <Divider sx={{ mb: 3 }} />
    </Grid>

    <Grid item xs={12} sm={6}>
      <TextField
        label="Nome"
        fullWidth
        defaultValue="Mario"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label="Cognome"
        fullWidth
        defaultValue="Rossi"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label="Codice Fiscale"
        fullWidth
        defaultValue="RSSMRA80A01H501U"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={6}>
      <TextField
        label="Data di Nascita"
        fullWidth
        defaultValue="01/01/1980"
        variant="outlined"
      />
    </Grid>

    <Grid item xs={12} sx={{ mt: 2 }}>
      <Typography variant="h6" color="primary" gutterBottom>
        Residenza
      </Typography>
      <Divider sx={{ mb: 3 }} />
    </Grid>

    <Grid item xs={12} sm={8}>
      <TextField
        label="Indirizzo"
        fullWidth
        defaultValue="Via Roma"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={4}>
      <TextField
        label="Civico"
        fullWidth
        defaultValue="10"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={4}>
      <TextField
        label="CAP"
        fullWidth
        defaultValue="00100"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={4}>
      <TextField
        label="Città"
        fullWidth
        defaultValue="Roma"
        variant="outlined"
      />
    </Grid>
    <Grid item xs={12} sm={4}>
      <TextField
        label="Provincia"
        fullWidth
        defaultValue="RM"
        variant="outlined"
      />
    </Grid>
  </Grid>
);

const VeicoliView = () => (
  <Box>
    <Typography variant="body2" color="textSecondary" mb={4}>
      Elenco dei veicoli associati a questo cliente. Puoi gestire le polizze e i
      dettagli tecnici.
    </Typography>

    <Grid container spacing={3}>
      {[
        {
          targa: "AB123CD",
          modello: "FIAT Panda",
          allestimento: "1.2 Fire Dynamic",
          scadenza: "15/06/2024",
        },
        {
          targa: "EF456GH",
          modello: "VOLKSWAGEN Golf",
          allestimento: "2.0 TDI Business",
          scadenza: "20/11/2024",
        },
      ].map((v, i) => (
        <Grid item xs={12} key={i}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: "16px !important" }}>
              <Grid container alignItems="center">
                <Grid item xs={12} sm={2}>
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      textAlign: "center",
                      border: "1px solid #ddd",
                      width: "fit-content",
                      minWidth: 100,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ letterSpacing: 1 }}
                    >
                      {v.targa}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {v.modello}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {v.allestimento}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography variant="caption" color="textSecondary">
                    PROSSIMA SCADENZA
                  </Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {v.scadenza}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={1} textAlign="right">
                  <IconButton>
                    <MoreVert />
                  </IconButton>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>

    <Box mt={4}>
      <Button variant="outlined" startIcon={<DirectionsCar />}>
        Aggiungi Nuovo Veicolo
      </Button>
    </Box>
  </Box>
);

const PreventiviView = () => (
  <Box>
    <Box display="flex" justifyContent="space-between" mb={4}>
      <Typography variant="body2" color="textSecondary">
        Storico dei preventivi generati per il cliente.
      </Typography>
      <Button size="small" variant="text" color="primary">
        Vedi tutti
      </Button>
    </Box>

    <List disablePadding>
      {[
        {
          tipo: "RC Auto",
          data: "10/01/2024",
          stato: "Aperto",
          importo: "450,00",
          badge: "warning",
        },
        {
          tipo: "CVT - Incendio e Furto",
          data: "05/01/2024",
          stato: "Chiuso",
          importo: "1.200,00",
          badge: "success",
        },
        {
          tipo: "Infortuni Conducente",
          data: "22/12/2023",
          stato: "Scaduto",
          importo: "85,00",
          badge: "error",
        },
      ].map((p, i) => (
        <ListItem
          key={i}
          divider={i !== 2}
          sx={{ py: 3, px: 0 }}
          secondaryAction={
            <Box textAlign="right">
              <Typography variant="h6" fontWeight="bold">
                € {p.importo}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  bgcolor: `${p.badge}.main`,
                  color: "white",
                  px: 1,
                  py: 0.2,
                  borderRadius: 1,
                  fontSize: "0.65rem",
                  textTransform: "uppercase",
                }}
              >
                {p.stato}
              </Typography>
            </Box>
          }
        >
          <ListItemIcon>
            <Assignment color="action" />
          </ListItemIcon>
          <ListItemText
            primary={p.tipo}
            secondary={`Data creazione: ${p.data}`}
            primaryTypographyProps={{ fontWeight: "bold" }}
          />
        </ListItem>
      ))}
    </List>
  </Box>
);

const ContattiView = () => (
  <Grid container spacing={4}>
    <Grid item xs={12} sm={6}>
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Email color="primary" sx={{ mr: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Email
            </Typography>
          </Box>
          <TextField fullWidth defaultValue="mario.rossi@example.com" />
          <Spacer mb={2} />
          <TextField
            fullWidth
            label="Email Secondaria"
            placeholder="Aggiungi email..."
          />
        </CardContent>
      </Card>
    </Grid>
    <Grid item xs={12} sm={6}>
      <Card variant="outlined">
        <CardContent>
          <Box display="flex" alignItems="center" mb={2}>
            <Phone color="primary" sx={{ mr: 2 }} />
            <Typography variant="subtitle1" fontWeight="bold">
              Telefono
            </Typography>
          </Box>
          <TextField fullWidth defaultValue="+39 333 1234567" />
          <Spacer mb={2} />
          <TextField
            fullWidth
            label="Telefono Fisso"
            placeholder="Aggiungi numero..."
          />
        </CardContent>
      </Card>
    </Grid>
  </Grid>
);

export default ExampleCRM;
